import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatPhone } from '../utils/formatters';
import { useToast } from '../context/ToastContext';

export interface Staff {
  id?: number;
  name: string;
  role: string;
  email: string;
  phone: string;
  is_active: boolean;
}

interface StaffFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  staffToEdit?: Staff | null;
}

const StaffForm: React.FC<StaffFormProps> = ({ onSuccess, onCancel, staffToEdit }) => {
  const { addToast } = useToast();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (staffToEdit) {
      setName(staffToEdit.name);
      setRole(staffToEdit.role);
      setEmail(staffToEdit.email);
      setPhone(formatPhone(staffToEdit.phone));
      setIsActive(staffToEdit.is_active);
    } else {
      setName('');
      setRole('');
      setEmail('');
      setPhone('');
      setIsActive(true);
    }
  }, [staffToEdit]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const staffData = { name, role, email, phone, is_active: isActive };

    try {
      if (staffToEdit && staffToEdit.id) {
        await api.patch(`staff/${staffToEdit.id}/`, staffData);
        addToast('Dados do colaborador atualizados!', 'success');
      } else {
        await api.post('staff/', staffData);
        addToast('Novo colaborador cadastrado!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save staff', error);
      addToast('Erro ao salvar colaborador.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{staffToEdit ? '📝 Editar Colaborador' : '✨ Novo Colaborador da Escola'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Identificação Profissional" icon="👤" />
        <div className="form-group">
          <label>Nome Completo</label>
          <input 
            type="text" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            placeholder="Digite o nome do colaborador"
            required 
          />
        </div>

        <div className="form-group">
          <label>Cargo / Função</label>
          <input 
            type="text" 
            value={role} 
            onChange={(e) => setRole(e.target.value)} 
            placeholder="Ex: Coordenador Pedagógico, Professor, Secretário"
            required 
          />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Contatos" icon="📧" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>E-mail Institucional</label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="email@conectaterra.com.br"
              required 
            />
          </div>
          <div className="form-group">
            <label>Telefone / Ramal</label>
            <input 
              type="text" 
              value={phone} 
              onChange={handlePhoneChange} 
              placeholder="(XX) XXXXX-XXXX"
              maxLength={15}
              required 
            />
          </div>
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={isActive} 
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: '20px', height: '20px', marginRight: '10px' }}
            />
            <span style={{ fontWeight: '600', color: isActive ? 'var(--success)' : 'var(--danger)' }}>
              {isActive ? 'Colaborador Ativo' : 'Colaborador Inativo'}
            </span>
          </label>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar Cadastro'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default StaffForm;
